import ExternalLink from '@/components/ExternalLink'
import { GridInformationWithHookForm } from 'vnetwork-ui'
import CompanyIcons from '@/icons/CompanyIcons'
import { updateCompany } from '@/modules/Company/services'
import { Company } from '@/modules/Company/types'
import { INDUSTRIES, INDUSTRY_OPTIONS } from '@/modules/Customer/constants'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

interface CompanyProfileProps {
    company: Company
    refetch: () => void
}

const CompanyProfile: React.FC<CompanyProfileProps> = ({
    company,
    refetch,
}) => {
    const { t } = useTranslation()
    const isReadOnly = useCustomerReadOnly()

    const handleUpdateCompany = async (values: Company) => {
        return updateCompany({
            company: {
                ...values,
                company_establish_date:
                    typeof values.company_establish_date === 'string'
                        ? dayjs(values.company_establish_date)
                        : values.company_establish_date,
            },
            company_id: company?.company_id,
        })
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: handleUpdateCompany },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('updated-success'),
            errorMessage: t('Something went wrong'),
            onSuccess: () => {
                refetch()
            },
        }
    )

    return (
        <GridInformationWithHookForm
            loading={isPending}
            data={[
                {
                    label: t('company_name'),
                    value: company?.company_name,
                    formItemProps: {
                        name: 'company_name',
                        type: 'textfield',
                    },
                },
                {
                    label: t('company_establish_date'),
                    value: company?.company_establish_date
                        ? dayjs(company?.company_establish_date).format(
                              'DD/MM/YYYY'
                          )
                        : '',
                    formItemProps: {
                        name: 'company_establish_date',
                        type: 'datepicker',
                    },
                },
                {
                    label: t('company_tax'),
                    value: company?.company_tax,
                    formItemProps: {
                        name: 'company_tax',
                        type: 'textfield',
                    },
                },
                {
                    label: t('company_address'),
                    value: company?.company_address,
                    formItemProps: {
                        name: 'company_address',
                        type: 'textfield',
                    },
                },
                {
                    label: t('company_industry'),
                    value: INDUSTRIES?.find(
                        (i) => i.value === company?.company_industry
                    )?.label,
                    formItemProps: {
                        name: 'company_industry',
                        type: 'select',
                        options: INDUSTRY_OPTIONS,
                    },
                },
                {
                    label: t('company_website'),
                    value: company?.company_website ? (
                        <ExternalLink link={company?.company_website}>
                            {company?.company_website}
                        </ExternalLink>
                    ) : (
                        '---'
                    ),
                    formItemProps: {
                        name: 'company_website',
                        type: 'textfield',
                    },
                },
            ]}
            className="mt-4"
            head={{
                title: t('company'),
                icon: <CompanyIcons />,
                // onEdit vẫn bắt buộc; chỉ disabledAction mới khoá được nút sửa
                disabledAction: isReadOnly,
            }}
            defaultFormValues={company}
            onEdit={mutateAsync}
            // schema={updateCompanySchema as any}
        />
    )
}

export default CompanyProfile
